import type { SrSalesOrderType } from '@plato/data/starrocks/types';
import { RocketIcon } from 'lucide-react';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { RadioList, RadioListItem } from '../../../components/ui/button-radio-list.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { api } from '../../../utils/api.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { BaseConfirmationDialog } from '../../dialog/confirmation-dialog.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { DraftOrderCreateDialog } from '../../sales-draft-new/views/create-draft-order-dialog.tsx';
import { useDetailQuoteOrder } from '../hooks/use-detail-quote-order.tsx';

type QuoteOrderReleaseDialogProps = {
    transactionId: string;
    type: SrSalesOrderType;
};

export const QuoteOrderReleaseDialog = DialogStateManager.create(
    ({ transactionId, type }: QuoteOrderReleaseDialogProps) => {
        const { remove } = useDialog();
        const { t } = useLocale('quotes-orders-actions');
        const { toast } = useToast();
        const { canSendSalesOrderPdfByEmail } = useAppConfig();
        const [sendEmail, setSendEmail] = useState(canSendSalesOrderPdfByEmail ? '1' : '0');
        const apiUtils = api.useUtils();

        const { data } = useDetailQuoteOrder({ transactionId });

        const { mutate: release, isPending } = api.platoUser.command.salesOrder.order.release.useMutation({
            async onSuccess() {
                await Promise.all([
                    apiUtils.platoUser.getManyQuotesOrders.getAggregates.invalidate(),
                    apiUtils.platoUser.getManyQuotesOrders.getWithFilters.invalidate(),
                    apiUtils.platoUser.grid.getDetailQuoteOrder.get.invalidate({ id: transactionId }),
                ]);

                toast({
                    icon: 'success',
                    title: t(`${type}__release_toast__success_title`),
                    description: (
                        <Trans
                            t={t}
                            i18nKey={`${type}__release_toast__success_description`}
                            components={{
                                linkTag: (
                                    <div
                                        className="text-blue-700 hover:underline"
                                        onClick={() => {
                                            void DialogStateManager.show(DraftOrderCreateDialog, {
                                                customerId: data?.header.customer_id,
                                            });
                                        }}
                                    />
                                ),
                            }}
                        />
                    ),
                });

                remove();
            },
            onError(error) {
                toast({
                    icon: 'warning',
                    title: 'Error',
                    description: error.message,
                });
            },
        });

        const handleRelease = (): void => {
            release({
                sales_order_id: transactionId,
                should_send_email: Boolean(Number(sendEmail)),
            });
        };

        return (
            <BaseConfirmationDialog
                icon={RocketIcon}
                title={t(`${type}__release_confirmation__title`)}
                description={t(`${type}__release_confirmation__description`)}
            >
                <div className="flex w-full">
                    <RadioList defaultValue={sendEmail} onValueChange={setSendEmail}>
                        <RadioListItem
                            disabled={!canSendSalesOrderPdfByEmail}
                            value="1"
                            id="1"
                            label={t('send_pdf_by_email')}
                        />
                        <RadioListItem value="0" id="0" label={t('do_not_send_pdf_by_email')} />
                    </RadioList>
                </div>
                <BaseConfirmationDialog.Actions>
                    <BaseConfirmationDialog.RejectButton onClick={remove}>
                        {t('form__cancel', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton onClick={handleRelease} isLoading={isPending}>
                        {t('form__release', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.AcceptButton>
                </BaseConfirmationDialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
